import { ApolloLink, FetchResult, Observable } from "@apollo/client";
import { logger } from "src/logger";

// -------------------------------------------------------------------------------------------------
// TEMPORARY DEV MOCK LINK (authgard-ui-changes branch).
//
// A dev-only Apollo link that intercepts the three Authgard operations locally so the UI can be
// exercised without depending on beta data or mutating real principals. Each operation is
// individually toggleable via DEV_MOCKS. apollo-client.tsx prepends `devMockLink` only when
// NODE_ENV === 'development', so tests and prod never see it.
//
//   - getScopeMappings: re-stubbed to treat every scope as AUTHGARD while the backend idpType enum
//     naming is being fixed separately.
//   - bulkGetMfaStatus: stubbed to hand out a deterministic spread of per-principal MFA statuses
//     (NO_RECORD / INITIATED / ONBOARDED / REINITIATED / REREGISTER / expired / failure) so every
//     card state is reachable in the demo without needing beta principals in each state.
//   - bulkExecuteAuthgardAdminOperation: mocked so demos never run real initiate/revoke/reset against
//     beta principals (MOCK_ADMIN_OP_FORCE_ERROR forces the error copy).
//
// REMOVAL: when the backend is live, delete this whole file and remove the devMockLink import + dev
// branch in apollo-client.tsx. (Drop the getScopeMappings stub once the enum naming is fixed.)
// -------------------------------------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

// TEMP stub (pending backend idpType enum fix): resolve every requested scope to idpType AUTHGARD
// so the dev server surfaces the scan2auth/FIDO2 management options. Shaped exactly like the real
// getScopeMappings response (idpType at results[*].success.scopeMapping.metadata.config.idpType).
const mockGetScopeMappingsData = (variables: Record<string, any>): Record<string, any> => {
    const scopes: string[] = variables?.getScopeMappingsInput?.scopes ?? [];
    const tenantId: string = variables?.getScopeMappingsInput?.tenantId ?? '';
    return {
        getScopeMappings: {
            __typename: 'GetScopeMappingsOutput',
            results: scopes.map(scope => ({
                __typename: 'ScopeMappingResultEntryGraphql',
                scope,
                success: {
                    __typename: 'ScopeMappingSuccessGraphql',
                    scopeMapping: {
                        __typename: 'ScopeMappingGraphql',
                        scope,
                        tenantId,
                        metadata: {
                            __typename: 'ScopeMappingMetadataGraphql',
                            config: { __typename: 'ScopeMappingConfigGraphql', idpType: 'AUTHGARD' },
                        },
                    },
                },
                failure: null,
            })),
        },
    };
};

// Set to an AuthgardAdminErrorCode string (e.g. 'ACTIVE_REGCODE_EXISTS') to make the mocked
// admin operation return a per-target failure instead of success — useful for demoing the error
// copy. Leave null for the happy path. (Written as an assertion so the truthiness check below is a
// real runtime toggle, not a statically-constant condition.)
const MOCK_ADMIN_OP_FORCE_ERROR = null as string | null;

// Generates a registration code in the Authgard V2 format: 8 alphanumeric characters. The alphabet
// omits visually ambiguous characters (0/O, 1/I) to match a plausible backend-issued code.
const randomRegCode = (): string => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return Array.from({ length: 8 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
};

const mockBulkExecuteAuthgardAdminOperationData = (variables: Record<string, any>): Record<string, any> => {
    const input = variables?.bulkExecuteAuthgardAdminOperationInput ?? {};
    const operation: string = input.operation ?? '';
    const targets: { principalArn: string }[] = input.targets ?? [];
    const nowMs = Date.now();

    // Derive the resulting status / expiry / code from the operation, matching the Authgard V2
    // contract: INITIATE issues a 3-day code (and covers re-invites — V1's REINITIATE is gone),
    // REREGISTER revokes and issues a 1-hour re-registration code, CANCEL_INITIATE returns the
    // user to NONINITIATED (the raw wire status; the UI normalizes unmapped statuses to NO_RECORD).
    const outcome = (): { userStatus: string | null; regCode: string | null; mfaExpiry: string | null } => {
        switch (operation) {
            case 'INITIATE':
                return { userStatus: 'INITIATED', regCode: '7X9KM2P4', mfaExpiry: new Date(nowMs + 3 * MS_PER_DAY).toISOString() };
            case 'REREGISTER':
                return { userStatus: 'REREGISTER', regCode: '3T5VQ8W2', mfaExpiry: new Date(nowMs + MS_PER_HOUR).toISOString() };
            case 'CANCEL_INITIATE':
                return { userStatus: 'NONINITIATED', regCode: null, mfaExpiry: null };
            case 'FETCH_REGCODE':
                // V2 rotates on fetch: the old code is invalidated and a fresh one returned, so a
                // new random code each call matches the rotation. Whether the real response also
                // carries userStatus/mfaExpiry is unverified live; null keeps status/expiry as-is.
                return { userStatus: null, regCode: randomRegCode(), mfaExpiry: null };
            default:
                return { userStatus: null, regCode: null, mfaExpiry: null };
        }
    };

    // Deterministic per-target failure spread so a bulk run surfaces success + failure rows without
    // forcing ALL targets to fail. Index 0 always succeeds, so single-target calls (the single-user
    // modal) stay on the happy path; every 4th target thereafter returns a failure. MOCK_ADMIN_OP_
    // FORCE_ERROR still overrides everything when set.
    const buildEntry = (principalArn: string, index: number) => {
        const forcedError = MOCK_ADMIN_OP_FORCE_ERROR
            ?? (index > 0 && index % 4 === 3 ? 'SERVER_ERROR' : null);
        if (forcedError) {
            return {
                __typename: 'AdminOperationResultEntryGraphql',
                principalArn,
                success: null,
                failure: {
                    __typename: 'AdminOperationFailureGraphql',
                    errorCode: forcedError,
                    errorMessage: `Mocked failure: ${forcedError}`,
                },
            };
        }
        const { userStatus, regCode, mfaExpiry } = outcome();
        return {
            __typename: 'AdminOperationResultEntryGraphql',
            principalArn,
            success: {
                __typename: 'AdminOperationSuccessGraphql',
                regCode,
                userStatus,
                mfaExpiry,
            },
            failure: null,
        };
    };

    return {
        bulkExecuteAuthgardAdminOperation: {
            __typename: 'BulkExecuteAuthgardAdminOperationOutput',
            operation,
            results: targets.map((target, index) => buildEntry(target.principalArn, index)),
        },
    };
};

// TEMP stub: hand out a deterministic spread of per-principal MFA statuses so every card/modal state
// is reachable in the demo without needing real beta principals in each state. The status is pinned
// by a hash of the principalArn, NOT by the principal's position in the request: the Users page
// hydrates a whole page in one batch while the modal's Refresh re-reads a single principal, so a
// position-derived status made the modal contradict the card that opened it (a principal 19th on the
// page read RESET_FIDO2 on load, then NO_RECORD after Refresh). Hashing the arn keeps a principal's
// status identical at any batch size while still spreading a page across the whole state space.
// Shaped exactly like the real bulkGetMfaStatus response
// (results[*].success.credentials[*].{credentialType,mfaStatus,mfaInviteExpiry,mfaInviteCreatedAt}).
// Each principal is given BOTH credential types (SCAN2AUTH + FIDO2) carrying the same status, so the
// mock is user-type agnostic — whichever provider the modal resolves (associate -> Scan2Auth,
// admin -> FIDO2) finds the assigned status.
type MfaVariant =
    | { kind: 'no_record' }
    | { kind: 'failure'; errorCode: string }
    | { kind: 'credential'; status: string; inviteAgeDays: number | null; inviteExpiryDays: number | null };

// Reorder/extend to taste; which principal lands on which variant is decided by variantFor below.
const MFA_STATUS_VARIANTS: MfaVariant[] = [
    { kind: 'no_record' },                                                                    // 0: not enrolled
    { kind: 'credential', status: 'INITIATED',   inviteAgeDays: 0,  inviteExpiryDays: 3 },    // 1: enrollment in progress
    { kind: 'credential', status: 'ONBOARDED',   inviteAgeDays: 12, inviteExpiryDays: null }, // 2: enrolled
    { kind: 'credential', status: 'REINITIATED', inviteAgeDays: 0,  inviteExpiryDays: 3 },    // 3: re-invited (V2 emits this on an INITIATE re-invite)
    { kind: 'credential', status: 'REREGISTER',  inviteAgeDays: 0,  inviteExpiryDays: 1 / 24 }, // 4: revoked, 1-hour re-registration invite pending
    { kind: 'credential', status: 'INITIATED',   inviteAgeDays: 10, inviteExpiryDays: -2 },   // 5: invite EXPIRED
    { kind: 'failure', errorCode: 'INTERNAL_SERVICE_ERROR' },                                 // 6: per-principal failure -> empty list
];

// djb2 over the principalArn. Any stable string hash works; this one is short, dependency-free, and
// spreads the arn's trailing uuid well enough that a page of 15-20 principals covers most variants.
// >>> 0 keeps it unsigned so the modulo below can't yield a negative index.
const hashArn = (principalArn: string): number => {
    let hash = 5381;
    for (let i = 0; i < principalArn.length; i++) {
        hash = (((hash << 5) + hash) ^ principalArn.charCodeAt(i)) >>> 0;
    }
    return hash;
};

/** A principal's variant, fixed by its arn — identical whether it's fetched in a page or alone. */
const variantFor = (principalArn: string): MfaVariant =>
    MFA_STATUS_VARIANTS[hashArn(principalArn) % MFA_STATUS_VARIANTS.length];

const mockBulkGetMfaStatusData = (variables: Record<string, any>): Record<string, any> => {
    const principalArns: string[] = variables?.bulkGetMfaStatusInput?.principalArns ?? [];
    const nowMs = Date.now();
    const isoOrNull = (days: number | null): string | null =>
        days === null ? null : new Date(nowMs + days * MS_PER_DAY).toISOString();

    const credentialsFor = (v: Extract<MfaVariant, { kind: 'credential' }>) =>
        ['SCAN2AUTH', 'FIDO2'].map(credentialType => ({
            __typename: 'MfaCredentialStatusGraphql',
            credentialType,
            mfaStatus: v.status,
            mfaInviteCreatedAt: isoOrNull(v.inviteAgeDays === null ? null : -v.inviteAgeDays),
            mfaInviteExpiry: isoOrNull(v.inviteExpiryDays),
        }));

    const buildEntry = (principalArn: string) => {
        const variant = variantFor(principalArn);
        if (variant.kind === 'failure') {
            return {
                __typename: 'MfaStatusResultEntryGraphql',
                principalArn,
                success: null,
                failure: {
                    __typename: 'MfaStatusFailureGraphql',
                    errorCode: variant.errorCode,
                    errorMessage: `Mocked failure: ${variant.errorCode}`,
                },
            };
        }
        // no_record -> empty credentials list (mfaStatusForProvider then resolves to NO_RECORD).
        const credentials = variant.kind === 'credential' ? credentialsFor(variant) : [];
        return {
            __typename: 'MfaStatusResultEntryGraphql',
            principalArn,
            success: { __typename: 'MfaStatusSuccessGraphql', credentials },
            failure: null,
        };
    };

    return {
        bulkGetMfaStatus: {
            __typename: 'BulkGetMfaStatusOutput',
            results: principalArns.map(buildEntry),
        },
    };
};

// Per-operation dev-mock toggles — flip a flag to switch that operation between the local mock
// (true) and the real beta endpoint (false). Only consulted in the dev server; getApolloClient
// gates the whole link on NODE_ENV === 'development'.
const DEV_MOCKS = {
    // Stubbed to idpType AUTHGARD while the backend enum naming is being fixed. Set false when it lands.
    getScopeMappings: true,
    // Stubbed with a deterministic per-principal status spread. Set false to read real beta status.
    bulkGetMfaStatus: true,
    // Mocked so demos never run real initiate/revoke against beta principals. Set false for real writes.
    bulkExecuteAuthgardAdminOperation: true,
};

export const devMockLink = new ApolloLink((operation, forward) => {
    let data: Record<string, any> | null = null;
    if (DEV_MOCKS.getScopeMappings && operation.operationName === 'getScopeMappings') {
        data = mockGetScopeMappingsData(operation.variables);
    } else if (DEV_MOCKS.bulkGetMfaStatus && operation.operationName === 'bulkGetMfaStatus') {
        data = mockBulkGetMfaStatusData(operation.variables);
    } else if (DEV_MOCKS.bulkExecuteAuthgardAdminOperation && operation.operationName === 'bulkExecuteAuthgardAdminOperation') {
        data = mockBulkExecuteAuthgardAdminOperationData(operation.variables);
    }

    if (data) {
        logger.warn(`[DEV MOCK] Serving mocked ${operation.operationName} response — ` +
            `remove devMockLink from apollo-client before raising a CR.`);
        return new Observable<FetchResult>(observer => {
            observer.next({ data });
            observer.complete();
        });
    }

    return forward(operation);
});
